import { IconCreatePermission, TrashIcon } from "@lhd/assets";
import Button from "@lhd/components/Button";
import { generateUuid } from "@lhd/features/Apps/utils";
import {
  useFetchOrganizations,
  useFetchWorkersWithNoFilterV2,
} from "@lhd/hooks";
import { Select } from "antd";
import { useCallback, useMemo, useState } from "react";
import { BiEdit, BiPlus, BiTrash } from "react-icons/bi";
import { FaRegEye } from "react-icons/fa";
import { Worker } from "types/worker.type";
import { Styles } from "./styled";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE, FONT_SIZE_VALUE } from "@lhd/constants";
import { DISPLAY_SIZES } from "@lhd/types";

interface AppItem {
  create: IItem[];
  view: IItem[];
  edit: IItem[];
  delete: IItem[];
  rowLength: number;
}

interface IItem {
  id: string;
  organizationId: string;
  positionId: string;
  workerId: string;
}

type SelectedKeys = "create" | "edit" | "view" | "delete";

const AppPermissionMockup: AppItem = {
  create: [
    {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    },
  ],
  view: [
    {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    },
  ],
  edit: [
    {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    },
    {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    },
    {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    },
  ],
  delete: [],
  rowLength: 3,
};

const TableAppPermission = () => {
  const { data: workerData } = useFetchWorkersWithNoFilterV2(1, 100000, {});
  const { data: organizationData } = useFetchOrganizations({
    nameOnly: true,
  });

  const [appPermission, setAppPermission] =
    useState<AppItem>(AppPermissionMockup);

  const workerName = useCallback((worker: Worker) => {
    const textRetired =
      worker.retired_date && dayjs().isAfter(dayjs(worker.retired_date), "date")
        ? " (退職)"
        : "";

    if (worker.full_name) {
      return (
        `${worker.full_name ? worker.full_name : ""} ${
          worker.full_name && worker.worker_no ? "-" : ""
        } ${worker.worker_no ? worker.worker_no : ""}` + textRetired
      );
    }

    return (
      `${worker?.person?.name ? worker.person.name : ""} ${
        worker.person.name && worker.worker_no ? "-" : ""
      } ${worker.worker_no ? worker.worker_no : ""}` + textRetired
    );
  }, []);

  const workerOptions = useMemo(() => {
    if (!workerData) return [];
    return workerData?.data?.map((item) => ({
      value: item.id,
      label: workerName(item),
    }));
  }, [workerData, workerName]);

  const organizationOptions = useMemo(() => {
    return (
      organizationData?.map((item) => ({
        value: item.id,
        label: item.name,
      })) || []
    );
  }, [organizationData]);

  const handleSelectOrganization = (val: any) => {};

  const handleAddNewRow = (type: SelectedKeys) => {
    const newAppPermission = { ...appPermission };

    if (!newAppPermission?.[type]) {
      newAppPermission[type] = [];
    }
    const newItem: IItem = {
      id: generateUuid(),
      organizationId: "",
      positionId: "",
      workerId: "",
    };

    newAppPermission[type] = [...newAppPermission[type], newItem];

    if (newAppPermission[type].length > newAppPermission.rowLength) {
      newAppPermission.rowLength = newAppPermission[type].length;
    }

    setAppPermission(newAppPermission);
  };

  const handleDeleteRow = (type: SelectedKeys, id: string) => {
    const newAppPermission = { ...appPermission };

    if (newAppPermission?.[type]) {
      newAppPermission[type] = newAppPermission?.[type].filter(
        (typeItem) => typeItem.id !== id,
      );
      const newMaxLength = Math.max(
        newAppPermission?.create?.length,
        newAppPermission?.view?.length,
        newAppPermission?.edit?.length,
        newAppPermission?.delete?.length,
      );

      if (newAppPermission.rowLength !== newMaxLength) {
        newAppPermission.rowLength = newMaxLength;
      }
    }

    setAppPermission(newAppPermission);
  };

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  return (
    <Styles text_scale={displaySize}>
      <div className="table-app-permission">
        <table>
          <thead>
            <tr>
              <td className="title-app-name" colSpan={4} rowSpan={3}>
                Bizシート新規追加権限
                <br />
                <IconCreatePermission />
              </td>
              <td
                className="title-permission-all-app width-title-2"
                rowSpan={5 + appPermission.rowLength}
              >
                Bizシート全体権限
              </td>
              <td className="title-delete no-border-right" colSpan={8}></td>
              <td
                className="title-delete no-border-left"
                colSpan={4}
                rowSpan={3}
              >
                閲覧＋編集＋ゴミ箱権限
                <br />
                <FaRegEye size={16} /> <BiEdit size={16} />
                <BiTrash size={16} />
              </td>
              <td className="title-app-action" rowSpan={3}></td>
            </tr>

            <tr>
              <td className="title-edit no-border-right" colSpan={4}></td>
              <td className="title-edit no-border-left" colSpan={4} rowSpan={2}>
                閲覧＋編集権限 <br /> <FaRegEye size={16} />{" "}
                <BiEdit size={16} />
              </td>
            </tr>

            <tr>
              <td className="title-confirm" colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: 6 }}>
                    閲覧権限
                    <br /> <FaRegEye size={16} />
                  </span>{" "}
                </div>
              </td>
            </tr>

            <tr>
              <td className="width-title-1">組織</td>
              <td className="width-title-1">役職</td>
              <td className="width-title-1">ﾕｰｻﾞｰ</td>
              <td className="width-action"></td>
              <td className="width-title-2 d-none"></td>
              <td className="table-head width-title-2">組織</td>
              <td className="table-head width-title-1">役職</td>
              <td className="table-head width-title-1">ﾕｰｻﾞｰ</td>
              <td className="table-head width-action"></td>
              <td className="table-head width-title-2">組織</td>
              <td className="table-head width-title-1">役職</td>
              <td className="table-head width-title-1">ﾕｰｻﾞｰ</td>
              <td className="table-head width-action"></td>
              <td className="table-head width-title-2">組織</td>
              <td className="table-head width-title-1">役職</td>
              <td className="table-head width-title-1">ﾕｰｻﾞｰ</td>
              <td className="table-head width-action"></td>
            </tr>

            {/* Content table */}
            {appPermission.rowLength > 0 &&
              [...(Array(appPermission.rowLength).keys() as any)].map(
                (item) => (
                  <tr key={item}>
                    {appPermission.create?.[item] ? (
                      <>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            options={workerOptions}
                            defaultValue={workerOptions[0]?.value}
                            mode="multiple"
                          />
                        </td>
                        <td
                          className="trash-icon"
                          onClick={() =>
                            handleDeleteRow(
                              "create",
                              appPermission.create?.[item].id,
                            )
                          }
                        >
                          <TrashIcon />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="no-border border-left"></td>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                      </>
                    )}

                    <td className="d-none"></td>

                    {appPermission.view?.[item] ? (
                      <>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            options={workerOptions}
                            defaultValue={workerOptions[0]?.value}
                            mode="multiple"
                          />
                        </td>
                        <td
                          className="trash-icon"
                          onClick={() =>
                            handleDeleteRow(
                              "view",
                              appPermission.view?.[item].id,
                            )
                          }
                        >
                          <TrashIcon />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border border-right"></td>
                      </>
                    )}

                    {appPermission.edit?.[item] ? (
                      <>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            options={workerOptions}
                            defaultValue={workerOptions[0]?.value}
                            mode="multiple"
                          />
                        </td>
                        <td
                          className="trash-icon"
                          onClick={() =>
                            handleDeleteRow(
                              "edit",
                              appPermission.edit?.[item].id,
                            )
                          }
                        >
                          <TrashIcon />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border border-right"></td>
                      </>
                    )}

                    {appPermission.delete?.[item] ? (
                      <>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            showSearch
                            options={organizationOptions}
                            defaultValue={organizationOptions[0]?.value}
                          />
                        </td>
                        <td>
                          <Select
                            className="test-select"
                            bordered={false}
                            options={workerOptions}
                            defaultValue={workerOptions[0]?.value}
                            mode="multiple"
                          />
                        </td>
                        <td
                          className="trash-icon"
                          onClick={() =>
                            handleDeleteRow(
                              "delete",
                              appPermission.delete?.[item].id,
                            )
                          }
                        >
                          <TrashIcon />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border"></td>
                        <td className="no-border border-right"></td>
                      </>
                    )}
                  </tr>
                ),
              )}

            <tr>
              <td className="no-border-top" colSpan={4} rowSpan={1}>
                <div className="d-flex justify-center full-h align-end pb-1">
                  <Button
                    label="行追加"
                    icon={
                      <div className="flex-center">
                        <BiPlus size={18} />
                      </div>
                    }
                    onClick={() => handleAddNewRow("create")}
                    style={{
                      fontSize: size,
                      width: "fit-content",
                    }}
                    displaySize={displaySize}
                  />
                </div>
              </td>
              <td className="d-none"></td>
              <td className="d-none"></td>
              <td className="d-none"></td>

              <td className="d-none"></td>

              <td className="no-border-top" colSpan={4} rowSpan={1}>
                <div className="d-flex justify-center full-h align-end pb-1">
                  <Button
                    label="行追加"
                    icon={
                      <div className="flex-center">
                        <BiPlus size={18} />
                      </div>
                    }
                    onClick={() => handleAddNewRow("view")}
                    style={{
                      fontSize: size,
                      width: "fit-content",
                    }}
                    displaySize={displaySize}
                  />
                </div>
              </td>
              <td className="d-none"></td>
              <td className="d-none"></td>
              <td className="d-none"></td>

              <td className="no-border-top" colSpan={4} rowSpan={1}>
                <div className="d-flex justify-center full-h align-end pb-1">
                  <Button
                    label="行追加"
                    icon={
                      <div className="flex-center">
                        <BiPlus size={18} />
                      </div>
                    }
                    onClick={() => handleAddNewRow("edit")}
                    style={{
                      fontSize: size,
                      width: "fit-content",
                    }}
                    displaySize={displaySize}
                  />
                </div>
              </td>
              <td className="d-none"></td>
              <td className="d-none"></td>
              <td className="d-none"></td>

              <td className="no-border-top" colSpan={4} rowSpan={1}>
                <div className="d-flex justify-center full-h align-end pb-1">
                  <Button
                    label="行追加"
                    icon={
                      <div className="flex-center">
                        <BiPlus size={18} />
                      </div>
                    }
                    onClick={() => handleAddNewRow("delete")}
                    style={{
                      fontSize: size,
                      width: "fit-content",
                    }}
                    displaySize={displaySize}
                  />
                </div>
              </td>
              <td className="d-none"></td>
              <td className="d-none"></td>
              <td className="d-none"></td>
            </tr>

            <tr>
              <td colSpan={4}></td>
              <td className="d-none"></td>
              <td className="d-none"></td>

              <td>初期値</td>

              <td colSpan={4}></td>
              <td className="d-none"></td>
              <td className="d-none"></td>
              <td className="d-none"></td>

              <td>優先する組織</td>
              <td>全</td>
              <td>---</td>
              <td></td>

              <td></td>
              <td colSpan={4}></td>
              <td className="d-none"></td>
              <td className="d-none"></td>
            </tr>
          </thead>
        </table>
      </div>
    </Styles>
  );
};
export default TableAppPermission;
